import React, { useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { useFormik, type FormikErrors } from 'formik';
import { array, boolean, object, string } from 'yup';
import dayjs from 'dayjs';
import type {
  HistoryRecords,
  ScholarshipQuestionData,
  EditEvaluationPermission,
  DimensionData,
  InterviewPanelAssessment,
  SubScoresRecords,
  FundingDimensionFormik,
  EligibilityCheck,
} from './types';
import { SYSTEM_SCORE, EVALUATION_PERMISSION_TYPE } from './constant';
import {
  researchTypeOptions,
  interviewModeOptions,
  interviewLanguageOptions,
  academicProgrammeOptions,
} from './options';
import { HistoryRecordDialog } from './HistoryRecordDialog';
import { SubScoresRecordDialog } from './SubScoresRecordDialog';
import { ScholarshipComment } from './ScholarshipComment';
import { HKPFSSubScores } from './HKPFSSubScores';
import { UPAFSubScores } from './UPAFSubScores';
import { EVALUATION_TEXT } from './lang';
import { SUPERVISOR_MONITOR_LIST_MODULE } from '../../constant';
import { DRC_ASSESSMENT_MODULE } from '../../../../Assessment';
import { useAuth } from '../../../../../contexts';
import {
  type InputTextProps,
  returnedComponent,
} from '../../../../Application/components/returnFieldComponent';
import {
  Alert,
  type AlertDetails,
  type ArcTweenProps,
  Checkbox,
  Accordion,
  ArcTween,
  Loading,
  Button,
  Dialog,
  type DialogDetails,
  Select,
  type ROLES_MODULE,
} from '../../../../../components';
import {
  useUpdateScholarshipNominationQuestion,
  useUpdateResearchAssessment,
  useOverWriteScholarshipDimension,
  useUpdatePanelInterview,
  useCheckEligibility,
  useUpdateAcademicProgramme,
  useCalculateTotalScholarshipScores,
  usePolishComments,
  type FirstDegreeAssessment,
  type ResearchAssessment,
} from '../../../graphql/NominationForm';
import { checkInvalidDate } from '../../../../Application/graphql';
import { PanelInterviewBox } from '../../../../Assessment/DRCAssessment/components/AssessmentDetails/PanelInterview/PanelInterviewBox';
import { getSimplifyRole, SimplifyRoles } from '../../../../../utils';

type EvaluationFormProps = {
  applicationId: string;
  academicProgramme: string;
  scores: ArcTweenProps[];
  UPAFDimension?: DimensionData[];
  HKPFSDimension?: DimensionData[];
  candidatesDimension: DimensionData[];
  sectionalScores: DimensionData[];
  UPAFHistory: SubScoresRecords[];
  HKPFSHistory: SubScoresRecords[];
  candidatesHistory: SubScoresRecords[];
  justification: string;
  endorsement: boolean;
  academicExcellenceQuestions: ScholarshipQuestionData[];
  researchPotentialQuestions: ScholarshipQuestionData[];
  strengthsQuestions: ScholarshipQuestionData[];
  distinctiveQualityQuestions: ScholarshipQuestionData[];
  firstDegreeAssessment: FirstDegreeAssessment;
  researchAssessment: ResearchAssessment[];
  evaluationVisiblePermission: EditEvaluationPermission;
  interviewPanel: InterviewPanelAssessment;
  eligibilityCheck: EligibilityCheck[];
  refetchResearchAssessment: () => void;
  refetchEvaluation: () => void;
};

type InterviewPanelForm = Omit<InterviewPanelAssessment, 'panelMembers'>;

type ExampleDialog = {
  open: boolean;
  commentNo: string;
  commentEg: string;
  criteria: string;
};

type ResearchFormik = {
  researchAssessment: ResearchAssessment[];
};

export const EvaluationForm: React.FC<EvaluationFormProps> = (
  props: EvaluationFormProps,
) => {
  const {
    applicationId,
    scores,
    UPAFDimension = [],
    HKPFSDimension = [],
    sectionalScores = [],
    UPAFHistory,
    HKPFSHistory,
    candidatesHistory,
    academicExcellenceQuestions,
    researchPotentialQuestions,
    distinctiveQualityQuestions,
    strengthsQuestions,
    firstDegreeAssessment,
    researchAssessment,
    evaluationVisiblePermission,
    interviewPanel,
    endorsement,
    eligibilityCheck,
    academicProgramme,
    refetchResearchAssessment,
    refetchEvaluation,
  } = props;

  const {
    academicPermit,
    researchPermit,
    strengthPermit,
    distinctiveQualityPermit,

    researchAssessEditable,
    interviewAssessEditable,
    calculateAllScoresPermit,
    endorsedPermit,
    academicProgrammePermit,

    HKPFSDimensionVisible,
    UPAFDimensionVisible,
    HKPFSDimensionEditable,
    UPAFDimensionEditable,
  } = evaluationVisiblePermission;
  const { role } = useAuth();
  const currentRole = getSimplifyRole(role as ROLES_MODULE);

  const allowedEditEndorsement =
    endorsedPermit === EVALUATION_PERMISSION_TYPE.EDIT;
  const allowedEditAcademicProgramme =
    academicProgrammePermit === EVALUATION_PERMISSION_TYPE.EDIT;

  const [openAlert, setOpenAlert] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const [exampleDialog, setExampleDialog] = useState<ExampleDialog>({
    open: false,
    commentNo: '',
    commentEg: '',
    criteria: '',
  });
  const [openPolishDialog, setOpenPolishDialog] = useState<boolean>(false);
  const [confirmDialogContent, setConfirmDialogContent] =
    useState<DialogDetails>({
      title: '',
    });

  const [openHistoryDialog, setOpenHistoryDialog] = useState<{
    open: boolean;
    historyRecords: HistoryRecords[];
  }>({
    open: false,
    historyRecords: [],
  });

  const [openSubScoresDialog, setOpenSubScoresDialog] = useState<{
    open: boolean;
    subScoresRecords: SubScoresRecords[];
  }>({
    open: false,
    subScoresRecords: [],
  });

  const { gpa, rankings, universityName, originalGPA, originalGPAOutOf } =
    firstDegreeAssessment;

  const handleCloseEgDialog = (): void => {
    setExampleDialog({
      open: false,
      commentNo: '',
      commentEg: '',
      criteria: '',
    });
  };
  const handleOpenEgDialog =
    (commentNo: string, commentEg: string, criteria: string) => (): void => {
      setExampleDialog({ open: true, commentNo, commentEg, criteria });
    };

  const handleCloseHistoryDialog = (): void => {
    setOpenHistoryDialog({ open: false, historyRecords: [] });
  };

  const handleOpenEditHistory = (history: HistoryRecords[]) => (): void => {
    setOpenHistoryDialog({ open: true, historyRecords: history });
  };

  const updateScholarshipNominationQuestion =
    useUpdateScholarshipNominationQuestion();
  const handleSaveComment =
    (questionNumber: string) =>
    async (comment: string): Promise<void> => {
      setLoading(true);
      try {
        await updateScholarshipNominationQuestion({
          variables: {
            input: {
              applicationId,
              questionNumber,
              comment,
            },
          },
        });
        refetchEvaluation();
        setAlertDetails({
          alertContent: EVALUATION_TEXT.ALERT_INFO.UPDATE_COMMENT_SUCCESS,
          alertType: 'success',
        });
      } catch (error) {
        setAlertDetails({
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        });
      }
      setLoading(false);
      setOpenAlert(true);
    };

  const updateResearchAssessment = useUpdateResearchAssessment();
  const handleSaveResearchAssessment = async (
    type: ResearchFormik,
  ): Promise<void> => {
    try {
      await updateResearchAssessment({
        variables: {
          input: {
            applicationId,
            researchAssessments: type.researchAssessment.map(assessment => ({
              type: assessment.type,
              _id: assessment._id,
            })),
          },
        },
      });
      setAlertDetails({
        alertContent: EVALUATION_TEXT.ALERT_INFO.UPDATE_RESEARCH_SUCCESS,
        alertType: 'success',
      });

      refetchResearchAssessment();
      refetchEvaluation();
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const researchFormik = useFormik<ResearchFormik>({
    initialValues: {
      researchAssessment,
    },
    validationSchema: object().shape({
      researchAssessment: array().of(
        object({
          type: string().required('Required'),
        }),
      ),
    }),
    onSubmit: handleSaveResearchAssessment,
  });

  const overWriteScholarshipDimension = useOverWriteScholarshipDimension();
  const handleOverWriteScholarshipDimension =
    (scoringSystem: string, candidate: boolean) =>
    async (input: FundingDimensionFormik): Promise<void> => {
      setLoading(true);
      try {
        await overWriteScholarshipDimension({
          variables: {
            applicationId,
            input: {
              applicationId,
              scores: [
                // TODO: Temporarily hide it and will expose it in the futureF
                // ...(scoringSystem === SYSTEM_SCORE.HKPFS // Add another form data to the scores
                //   ? UPAFDimension
                //   : HKPFSDimension
                // ).map(dimension => ({
                //   dimension: dimension.dimension,
                //   scoringSystem: dimension.scoringSystem,
                //   overrideScore: Number(dimension.overrideScore),
                // })),
                ...input.dimensions.map(dimension => ({
                  dimension: dimension.dimension,
                  scoringSystem: dimension.scoringSystem,
                  overrideScore: Number(dimension.overrideScore),
                })),
              ],
              candidate,
              justification: input.justification,
            },
          },
        });
        setAlertDetails({
          alertContent: EVALUATION_TEXT.ALERT_INFO.UPDATE_DIMENSION_SUCCESS,
          alertType: 'success',
        });
        refetchEvaluation();
      } catch (error) {
        setAlertDetails({
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        });
      }
      setOpenAlert(true);
      setLoading(false);
    };

  const handleOpenDimensionHistoryDialog =
    (scoringSystem: string, isCandidates: boolean) => (): void => {
      setOpenSubScoresDialog({
        open: true,
        subScoresRecords:
          scoringSystem === SYSTEM_SCORE.UPAF
            ? isCandidates
              ? candidatesHistory
              : UPAFHistory
            : HKPFSHistory,
      });
    };

  const handleCloseDimensionHistoryDialog = (): void => {
    setOpenSubScoresDialog({ open: false, subScoresRecords: [] });
  };

  const updateInterviewPanel = useUpdatePanelInterview();
  const handleSaveInterviewPanel = async (
    values: InterviewPanelForm,
  ): Promise<void> => {
    try {
      await updateInterviewPanel({
        variables: {
          input: {
            applicationId,
            ...values,
          },
        },
      });
      setAlertDetails({
        alertContent: EVALUATION_TEXT.ALERT_INFO.UPDATE_INTERVIEW_SUCCESS,
        alertType: 'success',
      });
      refetchEvaluation();
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const interviewAssessmentFormik = useFormik<InterviewPanelForm>({
    initialValues: {
      panelAgreement: interviewPanel.panelAgreement,
      interviewLanguage: interviewPanel.interviewLanguage,
      interviewMode: interviewPanel.interviewMode,
      interviewDate: interviewPanel.interviewDate,
    },
    validationSchema: object().shape({
      panelAgreement: boolean().required().oneOf([true]),
      interviewLanguage: string().required('Required'),
      interviewMode: string().required('Required'),
      interviewDate: string().required('Required'),
    }),
    onSubmit: handleSaveInterviewPanel,
  });

  const checkEligibility = useCheckEligibility();
  const handleCheckEligibility = async (values: {
    endorsement: boolean;
    eligibilityCheck: EligibilityCheck[];
  }): Promise<void> => {
    try {
      await checkEligibility({
        variables: {
          applicationId,
          eligibilityCheckInput: {
            endorsement: values.endorsement,
            eligibility: values.eligibilityCheck.map(check => ({
              funding: check.funding,
              checked: check.checked,
            })),
          },
        },
      });
      setAlertDetails({
        alertContent: EVALUATION_TEXT.ALERT_INFO.CHECK_ELIGIBILITY_SUCCESS,
        alertType: 'success',
      });
      refetchEvaluation();
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const dorpgConfirmationFormik = useFormik({
    initialValues: {
      eligibilityCheck,
      endorsement,
    },
    validationSchema: object().shape({
      eligibilityCheck: array()
        .of(
          object({
            checked: boolean().required(),
            fundingName: string().required(),
          }),
        )
        .test(values => values?.some(value => value.checked) ?? false),
      endorsement: boolean().required().oneOf([true]),
    }),
    onSubmit: handleCheckEligibility,
  });

  const updateAcademicProgramme = useUpdateAcademicProgramme();
  const handleSaveAcademicProgramme = async (values: {
    academicProgramme: string;
  }): Promise<void> => {
    try {
      await updateAcademicProgramme({
        variables: {
          applicationId,
          academicProgramme: values.academicProgramme,
        },
      });
      setAlertDetails({
        alertContent:
          EVALUATION_TEXT.ALERT_INFO.UPDATE_ACADEMIC_PROGRAMME_SUCCESS,
        alertType: 'success',
      });
      refetchEvaluation();
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const calculateTotalScholarshipScores = useCalculateTotalScholarshipScores();
  const handleCalculateTotalScores = async (): Promise<void> => {
    setLoading(true);
    try {
      await calculateTotalScholarshipScores({
        variables: {
          applicationId,
        },
      });
      setAlertDetails({
        alertContent: EVALUATION_TEXT.ALERT_INFO.CALCULATE_TOTAL_SCORES_SUCCESS,
        alertType: 'success',
      });
      refetchEvaluation();
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
    setLoading(false);
  };

  const handlePolishDialogClose = (): void => {
    setOpenPolishDialog(false);
  };
  const polishComments = usePolishComments();
  const handlePolishCommentsSubmit = async (): Promise<void> => {
    setLoading(true);
    setOpenPolishDialog(false);
    try {
      await polishComments({
        variables: {
          applicationId,
        },
      });
      setConfirmDialogContent(dialogMappings.navigateBackList);
      setOpenPolishDialog(true);
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
    setLoading(false);
  };

  const handleRouteBack = (): void => {
    window.location.href =
      currentRole === SimplifyRoles.SUPERVISOR
        ? `/internal/nominations?am=${SUPERVISOR_MONITOR_LIST_MODULE.POLISH_COMMENTS}`
        : `/internal/nominations?am=${DRC_ASSESSMENT_MODULE.POLISH_COMMENTS}`;
  };

  const academicProgrammeFormik = useFormik({
    initialValues: {
      academicProgramme,
    },
    validationSchema: object().shape({
      academicProgramme: string().required('Required'),
    }),
    onSubmit: handleSaveAcademicProgramme,
  });

  const interviewAssessmentDataset: Array<
    InputTextProps<InterviewPanelForm> & { isFullWidth?: boolean }
  > = [
    {
      name: 'panelAgreement',
      label: EVALUATION_TEXT.PANEL_AGREEMENT,
      type: 'checkbox',
      required: true,
      disabled: !interviewAssessEditable,
      isFullWidth: true,
    },
    {
      name: 'interviewMode',
      label: EVALUATION_TEXT.INTERVIEW_MODE,
      type: 'select',
      required: true,
      disabled: !interviewAssessEditable,
      options: interviewModeOptions,
      isFullWidth: false,
    },
    {
      name: 'interviewLanguage',
      label: EVALUATION_TEXT.INTERVIEW_LANGUAGE,
      type: 'select',
      required: true,
      disabled: !interviewAssessEditable,
      options: interviewLanguageOptions,
      isFullWidth: false,
    },
    {
      name: 'interviewDate',
      label: EVALUATION_TEXT.INTERVIEW_DATE,
      type: 'date',
      required: true,
      disabled: !interviewAssessEditable,
      isFullWidth: false,
      minDate: null,
      maxDate: null,
    },
  ];

  const mappingQuestions = (
    questions: ScholarshipQuestionData[],
    enableEdit: boolean,
  ): React.ReactNode => {
    return (
      <div className="grid grid-cols-2 gap-4 px-2">
        {questions.map((question, key) => {
          const {
            questionNumber,
            mappedNumber,
            questionTitle,
            questionExample,
            comment,
            history,
            lengthLimit,
            criteria,
          } = question;

          const savedDate = history?.[0]?.date;
          const savedAtData = checkInvalidDate(savedDate)
            ? ''
            : `${EVALUATION_TEXT.SAVE_AT} ${dayjs(savedDate).format('DD/MM/YYYY')} ${
                history?.[0]?.content[0]?.createdAt
              }`;

          const updatedQuestionTitle = `${questionTitle} (Max. ${lengthLimit} characters)`;
          return (
            <ScholarshipComment
              key={key}
              isFullWidth={mappedNumber === 'B3'} // Research Potential
              question={updatedQuestionTitle}
              comment={comment}
              enableEdit={enableEdit}
              savedAt={savedAtData}
              lengthLimit={lengthLimit}
              handleOpenEgDialog={handleOpenEgDialog(
                questionNumber,
                questionExample,
                criteria,
              )}
              handleOpenEditHistory={handleOpenEditHistory(history)}
              handleSaveComment={handleSaveComment(mappedNumber)}
            />
          );
        })}
      </div>
    );
  };

  const dialogMappings: Record<string, DialogDetails> = {
    confirmPolish: {
      title: '',
      content: EVALUATION_TEXT.POLISH_COMMENTS_CONFIRMATION_TITLE,
      confirmAction: EVALUATION_TEXT.CONFIRM,
      cancelAction: EVALUATION_TEXT.CANCEL,
      handleDialogConfirmAction: handlePolishCommentsSubmit,
    },
    navigateBackList: {
      title: '',
      content: EVALUATION_TEXT.POLISH_COMMENTS_CONFIRMATION_SUCCESS,
      confirmAction: EVALUATION_TEXT.YES,
      handleDialogConfirmAction: handleRouteBack,
    },
  };

  return (
    <div>
      {loading && <Loading />}
      {academicPermit !== EVALUATION_PERMISSION_TYPE.HIDE && (
        <Accordion
          className="w-full"
          title={EVALUATION_TEXT.ACADEMIC_EXCELLENCE}
          defaultExpanded
          details={
            <div className="p-2">
              {mappingQuestions(
                academicExcellenceQuestions,
                academicPermit === EVALUATION_PERMISSION_TYPE.EDIT,
              )}
              <div className="p-2">
                <div>
                  <div>
                    <b>
                      {EVALUATION_TEXT.UNIVERSITY_INFO}
                      {': '}
                    </b>
                  </div>
                  <Grid
                    container
                    direction="row"
                    columnGap={2}
                    rowGap={0}
                    sx={{
                      width: '100%',
                    }}
                  >
                    <Grid>
                      {EVALUATION_TEXT.UNIVERSITY_NAME}
                      {': '}
                      <span className="text-[#951D1D]">{universityName}</span>
                    </Grid>
                    {rankings.map((ranking, index) => {
                      return (
                        <Grid key={index}>
                          {ranking.rankingSystem}
                          {': '}
                          <span className="text-[#951D1D]">
                            {ranking.originalRanking}
                          </span>
                        </Grid>
                      );
                    })}
                  </Grid>
                </div>
                <div className="flex flex-row gap-8">
                  <b>
                    {EVALUATION_TEXT.GPA}
                    {': '}
                    <span className="text-[#951D1D]">{gpa + '/4.0'}</span>
                  </b>

                  <b>
                    {EVALUATION_TEXT.ORIGIN_GPA}
                    {': '}
                    <span className="text-[#951D1D]">
                      {originalGPA + '/' + originalGPAOutOf}
                    </span>
                  </b>
                </div>
              </div>
            </div>
          }
        />
      )}
      {/** Research Potential */}
      {researchPermit !== EVALUATION_PERMISSION_TYPE.HIDE && (
        <Accordion
          className="w-full"
          title={EVALUATION_TEXT.RESEARCH_POTENTIAL}
          defaultExpanded
          details={
            <div className="p-2">
              {researchFormik.values.researchAssessment.length > 0 && (
                <form onSubmit={researchFormik.handleSubmit}>
                  <div className="grid grid-cols-2 gap-6">
                    {researchFormik.values.researchAssessment.map(
                      (assessment, index) => {
                        const { type, publicationTitle } = assessment;
                        return (
                          <div
                            key={index}
                            className="flex flex-col justify-between"
                          >
                            <div className="flex flex-row mb-2 text-sm grid grid-cols-2 gap-2 ">
                              <span className="font-bold mr-2 break-words">
                                {EVALUATION_TEXT.JOURNAL_NAME}
                              </span>
                              <span>{publicationTitle}</span>
                            </div>
                            <div className="flex flex-row text-sm grid grid-cols-2 items-start">
                              <span className="font-bold mr-2 pb-10 pt-2">
                                {EVALUATION_TEXT.JOURNAL_TYPE}
                              </span>
                              <span>
                                <Select
                                  name={`researchAssessment.${index}.type`}
                                  value={type}
                                  size="small"
                                  fullWidth
                                  variant="outlined"
                                  options={researchTypeOptions}
                                  onChange={researchFormik.handleChange}
                                  onBlur={researchFormik.handleBlur}
                                  disabled={!researchAssessEditable}
                                  error={
                                    researchFormik.touched.researchAssessment &&
                                    researchFormik.touched.researchAssessment[
                                      index
                                    ]?.type &&
                                    Boolean(
                                      (
                                        (researchFormik.errors
                                          .researchAssessment ?? [])[
                                          index
                                        ] as FormikErrors<ResearchAssessment>
                                      )?.type ?? false,
                                    )
                                  }
                                  helperText={
                                    researchFormik.touched.researchAssessment &&
                                    researchFormik.touched.researchAssessment[
                                      index
                                    ]?.type &&
                                    (
                                      (researchFormik.errors
                                        .researchAssessment ?? [])[
                                        index
                                      ] as FormikErrors<ResearchAssessment>
                                    )?.type
                                  }
                                />
                              </span>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                  {researchAssessEditable && (
                    <div className="flex flex-row justify-center mt-4 mb-4">
                      <Button
                        type="submit"
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                      >
                        {EVALUATION_TEXT.SAVE}
                      </Button>
                    </div>
                  )}
                </form>
              )}
              {mappingQuestions(
                researchPotentialQuestions,
                researchPermit === EVALUATION_PERMISSION_TYPE.EDIT,
              )}
            </div>
          }
        />
      )}
      {/** Strengths */}
      {strengthPermit !== EVALUATION_PERMISSION_TYPE.HIDE && (
        <Accordion
          className="w-full"
          title={EVALUATION_TEXT.STRENGTHS}
          defaultExpanded
          details={
            <div>
              <h2 className="text-lg mt-4 ml-2 font-medium">
                {EVALUATION_TEXT.INTERVIEW_ASSESSMENT}
              </h2>
              <div className="m-2 mb-6 p-2 bg-[#F6F6F6] rounded">
                <div className="pl-2 pr-2">
                  {interviewPanel.panelMembers.length > 0 && (
                    <PanelInterviewBox
                      initialCheckbox
                      initialValues={interviewPanel.panelMembers}
                      handleSubmit={async () => {}}
                      refetchAssessment={refetchEvaluation}
                      isViewMode
                      hideDeclaration
                    />
                  )}
                </div>

                <form
                  onSubmit={interviewAssessmentFormik.handleSubmit}
                  noValidate
                  className="mt-4"
                >
                  <div className="grid grid-cols-3 gap-4 px-2">
                    {interviewAssessmentDataset.map(
                      (
                        item: InputTextProps<InterviewPanelForm> & {
                          isFullWidth?: boolean;
                        },
                        index: number,
                      ) => {
                        const { isFullWidth } = item;
                        return (
                          <div
                            key={index}
                            className={`w-full ${isFullWidth ? 'col-span-3' : ''}`}
                          >
                            {returnedComponent({
                              item,
                              formik: interviewAssessmentFormik,
                            })}
                          </div>
                        );
                      },
                    )}
                  </div>
                  {interviewAssessEditable && (
                    <div className="flex flex-row justify-center mt-4 mb-4">
                      <Button
                        type="submit"
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                      >
                        {EVALUATION_TEXT.SAVE}
                      </Button>
                    </div>
                  )}
                </form>
              </div>
              {mappingQuestions(
                strengthsQuestions,
                strengthPermit === EVALUATION_PERMISSION_TYPE.EDIT,
              )}
            </div>
          }
        />
      )}
      {/** Distinctive */}
      {distinctiveQualityPermit !== EVALUATION_PERMISSION_TYPE.HIDE && (
        <Accordion
          className="w-full"
          title={EVALUATION_TEXT.DISTINCTIVE_QUALITY}
          defaultExpanded
          details={mappingQuestions(
            distinctiveQualityQuestions,
            distinctiveQualityPermit === EVALUATION_PERMISSION_TYPE.EDIT,
          )}
        />
      )}
      {/* Calculate scores by all comments */}
      {calculateAllScoresPermit !== EVALUATION_PERMISSION_TYPE.HIDE && (
        <div className="mt-6">
          <div className="text-[#951D1D] ml-4 font-medium italic">
            {EVALUATION_TEXT.CALCULATE_SCORES_HELPER_TEXT}
          </div>
          <div className="flex flex-row items-center justify-center m-4 mb-10">
            <Button
              onClick={handleCalculateTotalScores}
              variant="contained"
              disabled={
                calculateAllScoresPermit !== EVALUATION_PERMISSION_TYPE.EDIT
              }
              sx={{
                textTransform: 'none',
                borderRadius: '0.4rem',
                padding: '0.5rem 2rem',
              }}
            >
              {EVALUATION_TEXT.CALCULATE_SCORES}
            </Button>
          </div>
        </div>
      )}
      {/** HKPFS Sub-scores */}
      {HKPFSDimensionVisible && (
        <HKPFSSubScores
          title={EVALUATION_TEXT.HKPFS_SUB_SCORES}
          dimensions={HKPFSDimension}
          dimensionEditable={HKPFSDimensionEditable}
          handleOpenDimensionHistoryDialog={handleOpenDimensionHistoryDialog(
            SYSTEM_SCORE.HKPFS,
            false,
          )}
          handleOverWriteScholarshipDimension={handleOverWriteScholarshipDimension(
            SYSTEM_SCORE.HKPFS,
            false,
          )}
        />
      )}
      {/** UPAF Sub-scores */}
      {UPAFDimensionVisible && (
        <UPAFSubScores
          title={EVALUATION_TEXT.SYSTEM_SCORES}
          dimensions={UPAFDimension}
          dimensionEditable={UPAFDimensionEditable}
          handleOpenDimensionHistoryDialog={handleOpenDimensionHistoryDialog(
            SYSTEM_SCORE.UPAF,
            false,
          )}
          handleOverWriteScholarshipDimension={handleOverWriteScholarshipDimension(
            SYSTEM_SCORE.UPAF,
            false,
          )}
          sectionalScores={sectionalScores}
        />
      )}
      {/** Funding Scores */}
      <div className="mb-6">
        <h2 className="text-lg font-bold ml-4">
          {EVALUATION_TEXT.FUNDING_SCORES}
        </h2>
        <div className="flex flex-row justify-center w-full bg-[#F6F6F6] mt-2 p-4 space-x-8 rounded flex-wrap">
          {scores.length > 0 ? (
            scores.map((fundingScore, index) => {
              return (
                <ArcTween
                  occupied={fundingScore.occupied}
                  total={fundingScore.total}
                  name={fundingScore.name}
                  description={EVALUATION_TEXT.SCORES_EXPLANATION}
                  key={index}
                />
              );
            })
          ) : (
            <>{EVALUATION_TEXT.NA}</>
          )}
        </div>
      </div>
      {/* Academic Programme Category */}
      {academicProgrammePermit !== EVALUATION_PERMISSION_TYPE.HIDE && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-2 ml-4">
            {EVALUATION_TEXT.ACADEMIC_PROGRAMME_CATEGORY}
          </h2>
          <form
            onSubmit={academicProgrammeFormik.handleSubmit}
            noValidate
            className="p-4 pl-8 flex flex-col bg-[#F6F6F6] rounded"
          >
            <div
              className="mb-4 font-medium"
              dangerouslySetInnerHTML={{
                __html: EVALUATION_TEXT.SELECT_ACADEMIC_PROGRAMME_CATEGORY,
              }}
            />
            <div className="flex flex-col mb-6 w-96">
              <Select
                options={academicProgrammeOptions}
                name="academicProgramme"
                value={academicProgrammeFormik.values.academicProgramme}
                onChange={academicProgrammeFormik.handleChange}
                onBlur={academicProgrammeFormik.handleBlur}
                disabled={!allowedEditAcademicProgramme}
                sx={{
                  '.MuiInputBase-root': {
                    background: '#ffffff',
                  },
                }}
                error={
                  academicProgrammeFormik.touched.academicProgramme &&
                  Boolean(academicProgrammeFormik.errors.academicProgramme)
                }
                helperText={
                  academicProgrammeFormik.touched.academicProgramme &&
                  academicProgrammeFormik.errors.academicProgramme
                }
              />
            </div>
            {allowedEditAcademicProgramme && (
              <div>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '0.4rem',
                    height: '2rem',
                  }}
                >
                  {EVALUATION_TEXT.SAVE}
                </Button>
              </div>
            )}
          </form>
        </div>
      )}
      {/* DORPG confirm */}
      {endorsedPermit !== EVALUATION_PERMISSION_TYPE.HIDE && (
        <form
          onSubmit={dorpgConfirmationFormik.handleSubmit}
          noValidate
          className="p-4 flex flex-col"
        >
          <h2 className="text-lg font-bold mb-4">
            {EVALUATION_TEXT.FUNDING_ELIGIBILITY_CONFIRM}
          </h2>
          <div
            className="text-base tracking-[.00938em] mb-2"
            style={{
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: allowedEditEndorsement
                  ? EVALUATION_TEXT.QUALIFICATION_RECOMMENDATION
                  : EVALUATION_TEXT.MONITOR_DORPG_ENDORSE,
              }}
            />
          </div>
          <div className="ml-4 flex flex-col mb-6">
            {dorpgConfirmationFormik.values.eligibilityCheck.length > 0 &&
              dorpgConfirmationFormik.values.eligibilityCheck.map(
                (check, index) => {
                  const isValidDeadline = !checkInvalidDate(
                    check.fundingDeadline,
                  );
                  return (
                    <span key={index} className="flex flex-row items-center">
                      <Checkbox
                        name={`eligibilityCheck.${index}.checked`}
                        value={check.checked}
                        label={check.fundingName}
                        checked={check.checked}
                        disabled={!allowedEditEndorsement}
                        error={
                          dorpgConfirmationFormik.touched.eligibilityCheck &&
                          dorpgConfirmationFormik.touched.eligibilityCheck[
                            index
                          ]?.checked &&
                          Boolean(
                            dorpgConfirmationFormik.errors.eligibilityCheck,
                          )
                        }
                        onBlur={dorpgConfirmationFormik.handleBlur}
                        onChange={dorpgConfirmationFormik.handleChange}
                      />
                      {isValidDeadline && (
                        <span className="text-[#951D1D] italic">
                          {EVALUATION_TEXT.DEADLINE +
                            ' ' +
                            dayjs(check.fundingDeadline).format('DD/MM/YYYY')}
                        </span>
                      )}
                    </span>
                  );
                },
              )}
          </div>
          {allowedEditEndorsement && (
            <>
              <Checkbox
                name="endorsement"
                checked={dorpgConfirmationFormik.values.endorsement}
                label={
                  <div className="!mt-2 font-bold">
                    {EVALUATION_TEXT.DORPG_CONFIRMATION}
                  </div>
                }
                sx={{
                  alignItems: 'flex-start',
                }}
                value={dorpgConfirmationFormik.values.endorsement}
                disabled={!allowedEditEndorsement}
                onChange={dorpgConfirmationFormik.handleChange}
                onBlur={dorpgConfirmationFormik.handleBlur}
                error={
                  dorpgConfirmationFormik.touched.endorsement &&
                  Boolean(dorpgConfirmationFormik.errors.endorsement)
                }
              />
              <div className="w-full flex flex-col items-center mt-10 mb-10">
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    padding: '0.5rem 2rem',
                  }}
                >
                  {EVALUATION_TEXT.CONFIRM}
                </Button>
              </div>
            </>
          )}
        </form>
      )}
      <Dialog
        openDialog={exampleDialog.open}
        width={'38rem'}
        dialogTitle={
          EVALUATION_TEXT.COMMENT_GUIDELINE + ' & ' + EVALUATION_TEXT.COMMENT_EG
        }
        cancelAction={EVALUATION_TEXT.CLOSE}
        onDialogClose={handleCloseEgDialog}
        displayCloseButton
      >
        <div className="overflow-auto">
          <div className="px-6 pb-4">
            <div className="text-[#951D1D] font-medium">
              {EVALUATION_TEXT.COMMENT_GUIDELINE + ':'}
            </div>
            <div className="bg-white border-solid border-2 border-[#ECECEC] p-4 h-[14rem] overflow-auto rounded">
              <div
                dangerouslySetInnerHTML={{
                  __html: exampleDialog.criteria,
                }}
              />
            </div>
          </div>
          <div className="px-6 pb-4">
            <div className="text-[#951D1D] font-medium">
              {EVALUATION_TEXT.COMMENT_EG + ':'}
            </div>
            <div className="bg-white border-solid border-2 border-[#ECECEC] p-4 h-[14rem] overflow-auto rounded">
              <div
                dangerouslySetInnerHTML={{
                  __html: exampleDialog.commentEg,
                }}
              />
            </div>
          </div>
        </div>
      </Dialog>
      <Dialog
        openDialog={openPolishDialog}
        dialogTitle={''}
        dialogAction={confirmDialogContent.confirmAction}
        cancelAction={confirmDialogContent.cancelAction}
        onDialogClose={handlePolishDialogClose}
        onDialogAction={confirmDialogContent.handleDialogConfirmAction}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {confirmDialogContent.content}
        </Typography>
      </Dialog>
      <HistoryRecordDialog
        dialogTitle={EVALUATION_TEXT.EDIT_HISTORY}
        openDialog={openHistoryDialog.open}
        historyRecords={openHistoryDialog.historyRecords}
        cancelAction={EVALUATION_TEXT.CLOSE}
        onDialogClose={handleCloseHistoryDialog}
      />
      {openSubScoresDialog.open && ( // The component would render even if not using it
        <SubScoresRecordDialog
          dialogTitle={EVALUATION_TEXT.EDIT_HISTORY}
          openDialog={openSubScoresDialog.open}
          historyRecords={openSubScoresDialog.subScoresRecords}
          cancelAction={EVALUATION_TEXT.CLOSE}
          onDialogClose={handleCloseDimensionHistoryDialog}
        />
      )}
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </div>
  );
};
